import Highcharts from 'highcharts';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import localeData from 'dayjs/plugin/localeData';
import { CHART_COLORS as UI, COLOR_PALETTE } from './chartColors';
import { FONT_FAMILY, DATA_LABEL_STYLE } from './chartTheme';
import { capitalizeFirstLetter } from '../../utils/helpers';

dayjs.extend(localeData);
dayjs.locale('es');

const cap = (s: string) => capitalizeFirstLetter(s.replace(/\.$/, ''));

Highcharts.setOptions({
  colors: COLOR_PALETTE,
  accessibility: {
    enabled: false,
  },
  lang: {
    months: dayjs.months().map(cap),
    shortMonths: dayjs.monthsShort().map(cap),
    weekdays: dayjs.weekdays().map(cap),
    shortWeekdays: dayjs.weekdaysShort().map(cap),
    decimalPoint: ',',
    thousandsSep: '.',
    noData: 'Sin datos',
    loading: 'Cargando...',
    resetZoom: 'Resetear zoom',
    resetZoomTitle: 'Volver al zoom 1:1',
  },
  chart: {
    style: {
      fontFamily: FONT_FAMILY,
    },
    backgroundColor: 'transparent',
    spacingTop: 12,
    spacingBottom: 12,
  },
  title: {
    style: {
      color: UI.textPrimary,
      fontSize: '14px',
      fontWeight: '600',
      letterSpacing: '-0.01em',
      fontFamily: FONT_FAMILY,
    },
  },
  subtitle: {
    style: {
      color: UI.textMuted,
      fontSize: '12px',
      fontFamily: FONT_FAMILY,
    },
  },
  xAxis: {
    labels: {
      style: {
        color: UI.textMuted,
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
      },
    },
    title: {
      style: {
        color: UI.textSecondary,
        fontSize: '12px',
        fontWeight: '600',
        fontFamily: FONT_FAMILY,
      },
    },
    lineColor: UI.border,
    tickColor: UI.border,
    gridLineColor: UI.grid,
  },
  yAxis: {
    labels: {
      style: {
        color: UI.textMuted,
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
      },
    },
    title: {
      style: {
        color: UI.textSecondary,
        fontSize: '12px',
        fontWeight: '600',
        fontFamily: FONT_FAMILY,
      },
    },
    gridLineColor: UI.grid,
    lineColor: UI.border,
  },
  tooltip: {
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    borderColor: UI.border,
    borderRadius: 10,
    borderWidth: 1,
    shadow: {
      color: 'rgba(15, 23, 42, 0.12)',
      offsetX: 0,
      offsetY: 4,
      opacity: 0.5,
      width: 10,
    },
    padding: 10,
    style: {
      color: UI.textPrimary,
      fontSize: '12px',
      fontFamily: FONT_FAMILY,
    },
    headerFormat: `<span style="font-size: 11px; color: ${UI.textMuted}; font-weight: 600;">{point.key}</span><br/>`,
  },
  legend: {
    itemStyle: {
      color: UI.textSecondary,
      fontSize: '12px',
      fontWeight: '500',
      fontFamily: FONT_FAMILY,
    },
    itemHoverStyle: {
      color: UI.textPrimary,
    },
    itemHiddenStyle: {
      color: UI.borderStrong,
    },
  },
  credits: {
    enabled: false,
  },
  plotOptions: {
    series: {
      animation: { duration: 300 },
      marker: {
        radius: 3,
        lineWidth: 1.5,
      },
      states: {
        hover: {
          lineWidthPlus: 1,
          halo: { size: 6, opacity: 0.18 },
        },
      },
    },
    line: {
      lineWidth: 2,
    },
    column: {
      // Sin stacking global: charts agrupan barras lado a lado.
      // Charts que necesitan stacking lo activan inline (ej. IrrigationChart).
      grouping: true,
      groupPadding: 0.1,
      pointPadding: 0.05,
      borderWidth: 0,
      borderRadius: 2,
      dataLabels: {
        enabled: true,
        format: '{y:.1f}',
        inside: false,
        verticalAlign: 'top',
        y: -2,
        overflow: 'allow',
        crop: false,
        style: DATA_LABEL_STYLE,
        filter: {
          property: 'y',
          operator: '>',
          value: 0.05,
        },
      },
    },
    bar: {
      grouping: true,
      groupPadding: 0.1,
      pointPadding: 0.05,
      borderWidth: 0,
      borderRadius: 2,
      dataLabels: {
        enabled: true,
        format: '{y:.1f}',
        inside: false,
        align: 'left',
        y: 0,
        overflow: 'allow',
        crop: false,
        style: DATA_LABEL_STYLE,
      },
    },
  },
  noData: {
    style: {
      color: UI.textDisabled,
      fontSize: '13px',
      fontWeight: '500',
      fontFamily: FONT_FAMILY,
    },
  },
});

export default Highcharts;
